import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
  X, MapPin, Layers, CalendarRange, Building2, AlertTriangle,
  ChevronRight, Loader2, CheckCircle2, RefreshCw,
} from 'lucide-react';
import type { RootState } from '@/state/store';
import { loginSuccess, setAccessibleCompanies } from '@/state/authSlice';
import { setSession } from '@/state/sessionSlice';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { apiClient } from '@/api/client';
import { authApi } from '@/api/auth.api';
import { broadcastCompanyChange } from '@/utils/crossTabSync';
import type { LoginResponse, SelectContextRequest, ContextLocation, ContextCostCenter, ContextCompany } from '@/types/auth.types';
import { costCentresForLocation } from '@/utils/costCentreScope';
import type { FinancialYearListDto } from '@/types/financialYear.types';

interface Props {
  open: boolean;
  onClose: () => void;
}

// ── Thin animated overlay ─────────────────────────────────────────────────────
const Backdrop = ({ onClick }: { onClick: () => void }) => (
  <div
    className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] animate-fade-in"
    onClick={onClick}
  />
);

// ── Dropdown row ──────────────────────────────────────────────────────────────
const DropdownField = ({
  icon: Icon,
  label,
  value,
  onChange,
  disabled,
  options,
  placeholder,
}: {
  icon:        React.ElementType;
  label:       string;
  value:       string;
  onChange:    (v: string) => void;
  disabled?:   boolean;
  options:     { value: string; label: string }[];
  placeholder?: string;
}) => (
  <div>
    <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-[0.07em] mb-1.5">
      <Icon size={11} />
      {label}
    </label>
    <div className="relative">
      <select
        value={value}
        onChange={e => onChange(e.target.value)}
        disabled={disabled || options.length <= 1}
        className="w-full px-3 py-2 pr-8 text-[13px] rounded-lg border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 appearance-none transition-all disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {placeholder && <option value="">{placeholder}</option>}
        {options.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
      <ChevronRight
        size={13}
        className="absolute right-2.5 top-1/2 -translate-y-1/2 rotate-90 text-gray-400 pointer-events-none"
      />
    </div>
    {options.length <= 1 && !placeholder && (
      <p className="text-[10px] text-gray-400 mt-1 pl-0.5">Only one option — auto-selected</p>
    )}
  </div>
);

// ── Main component ────────────────────────────────────────────────────────────
export const ContextSwitcherModal = ({ open, onClose }: Props) => {
  const dispatch  = useAppDispatch();
  const navigate  = useNavigate();
  const auth      = useSelector((s: RootState) => s.auth);
  const session   = useSelector((s: RootState) => s.session.session);

  const accessibleLocations   = auth.accessibleLocations   ?? [];
  const accessibleCostCenters = auth.accessibleCostCenters ?? [];

  // Current selections — seeded from session
  const [locationId,   setLocationId]   = useState<string>(session?.locationId   ?? '');
  const [costCenterId, setCostCenterId] = useState<string>(session?.costCenterId ?? '');
  const [fyId,         setFyId]         = useState<string>(session?.financialYearId ?? '');

  // Financial years fetched on open
  const [financialYears, setFinancialYears] = useState<FinancialYearListDto[]>([]);
  const [loadingFYs,     setLoadingFYs]     = useState(false);

  const [applying,  setApplying]  = useState(false);
  const [error,     setError]     = useState<string | null>(null);
  const [success,   setSuccess]   = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);

  // ── Company (ENH-001) ─────────────────────────────────────────────────────
  // freshCompanies: fetched live when the modal opens so we always show the current
  // grant, even if the user was given access to new companies since they logged in.
  // Falls back to Redux state only if the fetch hasn't completed yet.
  const [freshCompanies, setFreshCompanies] = useState<ContextCompany[] | null>(null);
  const accessibleCompanies: ContextCompany[] =
    freshCompanies ?? (auth.accessibleCompanies ?? []);
  const [companyId, setCompanyId] = useState('');
  const isCompanySwitch = !!companyId && companyId !== session?.companyId;

  // ── Helpers (defined before hooks that reference them) ────────────────────────

  const filteredCCs = (locId: string): ContextCostCenter[] =>
    costCentresForLocation(accessibleCostCenters, locId);

  const companyOptions = accessibleCompanies.map((c: ContextCompany) => ({
    value: c.id,
    label: `${c.code} — ${c.name}`,
  }));

  const locOptions = accessibleLocations.map((l: ContextLocation) => ({
    value: l.id,
    label: `${l.code} — ${l.name}${l.city ? ` (${l.city})` : ''}`,
  }));

  const ccOptions = filteredCCs(locationId).map((cc: ContextCostCenter) => ({
    value: cc.id,
    label: `${cc.code} — ${cc.name}`,
  }));

  const fyOptions = financialYears.map(fy => ({
    value: fy.id,
    label: `${fy.yearCode} · ${fy.description}`,
  }));

  // Fetch FY list when modal opens
  useEffect(() => {
    if (!open) return;

    // Reset state
    setError(null);
    setSuccess(false);
    setCompanyId(session?.companyId ?? '');
    setLocationId(session?.locationId   ?? '');
    setCostCenterId(session?.costCenterId ?? '');
    setFyId(session?.financialYearId ?? '');

    setLoadingFYs(true);
    apiClient
      .get('/financial-years?pageSize=30&sortBy=yearCode&sortDescending=true')
      .then(res => {
        const items: FinancialYearListDto[] = res.data?.data?.items ?? [];
        setFinancialYears(items);
        // Default to current session FY if set, else current year
        if (!session?.financialYearId) {
          const cur = items.find(y => y.isCurrent) ?? items[0];
          if (cur) setFyId(cur.id);
        }
      })
      .catch(() => setFinancialYears([]))
      .finally(() => setLoadingFYs(false));

    // Fetch the live company list — bypasses stale Redux state (set at login time)
    // so the picker always reflects grants made by an admin since the user last logged in.
    setFreshCompanies(null);
    authApi.accessibleCompanies()
      .then(res => {
        const raw = res.data?.data ?? [];
        const list: ContextCompany[] = raw.map((c: ContextCompany) => ({
          id:               c.id,
          code:             c.code,
          name:             c.name,
          baseCurrencyCode: c.baseCurrencyCode,
          isDefault:        c.isDefault,
        }));
        setFreshCompanies(list);
        dispatch(setAccessibleCompanies(list)); // keep Redux in sync for rest of app
      })
      .catch(() => { /* silently ignore — stale Redux list is still usable */ });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Re-filter cost center when location changes
  useEffect(() => {
    if (!locationId) return;
    const filtered = filteredCCs(locationId);

    // Cleared, not left alone, when the new location has no cost centre of its own: the previous
    // location's would otherwise stay selected and be submitted against this one (ML-02).
    if (!filtered.find(cc => cc.id === costCenterId)) {
      setCostCenterId(filtered[0]?.id ?? '');
    }
  }, [locationId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  // ── Apply ────────────────────────────────────────────────────────────────────

  const handleApply = async () => {
    if (!auth.accessToken) return;
    setApplying(true);
    setError(null);

    try {
      const selLoc = accessibleLocations.find(l => l.id === locationId);
      const selCC  = accessibleCostCenters.find(c => c.id === costCenterId);
      const selFY  = financialYears.find(y => y.id === fyId);
      // Resolve the target company so we can set its code/name in session
      const selCo  = accessibleCompanies.find(c => c.id === companyId);

      // On a company switch the location/cost centre on screen belong to the company we are
      // LEAVING — sending them would ask the server to enter the new company at the old one's
      // site. The server re-resolves against the target and would reject them anyway; omitting
      // them is how the client says it understands that.
      const payload: SelectContextRequest = {
        companyId:         isCompanySwitch ? companyId : undefined,
        locationId:        isCompanySwitch ? undefined : selLoc?.id || undefined,
        locationCode:      isCompanySwitch ? undefined : selLoc?.code || undefined,
        locationName:      isCompanySwitch ? undefined : selLoc?.name || undefined,
        costCenterId:      isCompanySwitch ? undefined : selCC?.id   || undefined,
        costCenterCode:    isCompanySwitch ? undefined : selCC?.code || undefined,
        costCenterName:    isCompanySwitch ? undefined : selCC?.name || undefined,
        financialYearId:   selFY?.id        || undefined,
        financialYearCode: selFY?.yearCode  || undefined,
      };

      const res = await apiClient.post<{ success: boolean; data?: LoginResponse; message?: string }>(
        '/auth/select-context',
        payload,
      );

      if (!res.data.success || !res.data.data) {
        setError(res.data.message || 'Failed to switch context.');
        return;
      }

      const enriched = res.data.data;

      dispatch(setSession({
        companyId:    enriched.user.companyId,
        // On a company switch use the target company's code/name from the picker.
        // Without this the old company name stays in the title bar.
        companyCode:  isCompanySwitch ? (selCo?.code ?? '') : (session?.companyCode ?? ''),
        companyName:  isCompanySwitch ? (selCo?.name ?? '') : (session?.companyName ?? ''),
        companyCurrency: enriched.user.baseCurrencyCode ?? selCo?.baseCurrencyCode ?? session?.companyCurrency ?? 'USD',

        // On a company switch the server re-resolves location/CC to the new company's defaults.
        // Use what the server echoes back; don't keep the old company's values.
        locationId:   isCompanySwitch ? (enriched.user.locationId   ?? null) : (selLoc?.id   ?? enriched.user.locationId   ?? null),
        locationCode: isCompanySwitch ? (enriched.user.locationCode ?? null) : (selLoc?.code ?? enriched.user.locationCode ?? null),
        locationName: isCompanySwitch ? (enriched.user.locationName ?? null) : (selLoc?.name ?? enriched.user.locationName ?? null),

        costCenterId:   isCompanySwitch ? (enriched.user.costCenterId   ?? null) : (selCC?.id   ?? enriched.user.costCenterId   ?? null),
        costCenterCode: isCompanySwitch ? (enriched.user.costCenterCode ?? null) : (selCC?.code ?? enriched.user.costCenterCode ?? null),
        costCenterName: isCompanySwitch ? (enriched.user.costCenterName ?? null) : (selCC?.name ?? enriched.user.costCenterName ?? null),

        financialYearId:          selFY?.id          ?? null,
        financialYearCode:        selFY?.yearCode     ?? null,
        financialYearDescription: selFY?.description ?? null,
      }));

      dispatch(loginSuccess(enriched));

      // Changed based on Dinisuru's request: Notify other open tabs so they do not post to the previous company
      if (isCompanySwitch) {
        broadcastCompanyChange(
          enriched.user.companyId,
          selCo?.code ?? '',
          selCo?.name ?? ''
        );
      }

      setSuccess(true);
      setTimeout(() => {
        setSuccess(false);
        onClose();
        // Company switch: navigate home so no screen retains stale data from the previous company.
        if (isCompanySwitch) navigate('/');
      }, 800);
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setApplying(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <>
      <Backdrop onClick={onClose} />

      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Switch Context"
        className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm mx-auto animate-fade-in"
      >
        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl ring-1 ring-gray-200 dark:ring-gray-700 overflow-hidden">

          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-blue-50 dark:bg-blue-900/30 flex items-center justify-center">
                <RefreshCw size={13} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="text-[14px] font-semibold text-gray-900 dark:text-gray-100 leading-tight">
                  Switch Context
                </h2>
                <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-tight">
                  Change company, location, cost center or financial year
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <X size={15} />
            </button>
          </div>

          {/* Body */}
          <div className="px-5 py-4 space-y-4">

            {/* Error */}
            {error && (
              <div className="text-[12px] text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 rounded-lg px-3 py-2 border border-red-200 dark:border-red-800">
                {error}
              </div>
            )}

            {/* Success flash */}
            {success && (
              <div className="flex items-center gap-2 text-[12px] text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20 rounded-lg px-3 py-2 border border-green-200 dark:border-green-800">
                <CheckCircle2 size={13} />
                Context updated!
              </div>
            )}

            {/* Company (ENH-001) — only when there is a choice to make. A single-company user
                should not be shown a control that does nothing. */}
            {companyOptions.length > 1 && (
              <DropdownField
                icon={Building2}
                label="Company"
                value={companyId}
                onChange={v => setCompanyId(v)}
                options={companyOptions}
              />
            )}

            {/* Switching company changes which books you are looking at, so it is called out
                rather than applied quietly alongside a cost-centre tweak. */}
            {isCompanySwitch && (
              <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                <AlertTriangle size={13} className="mt-0.5 shrink-0" />
                <span>
                  You are switching company. Unsaved work on open screens will be lost, and the
                  location and cost centre will reset to those of the company you are entering.
                </span>
              </div>
            )}

            {/* Location & Cost Center — hidden when switching company.
                The new company's defaults are resolved server-side; showing the
                current company's sites here would mislead the user. */}
            {!isCompanySwitch && locOptions.length > 0 && (
              <DropdownField
                icon={MapPin}
                label="Location"
                value={locationId}
                onChange={v => setLocationId(v)}
                options={locOptions}
              />
            )}

            {!isCompanySwitch && ccOptions.length > 0 && (
              <DropdownField
                icon={Layers}
                label="Cost Center"
                value={costCenterId}
                onChange={v => setCostCenterId(v)}
                options={ccOptions}
              />
            )}

            {/* Financial Year */}
            <DropdownField
              icon={CalendarRange}
              label="Financial Year"
              value={fyId}
              onChange={v => setFyId(v)}
              disabled={loadingFYs}
              options={fyOptions}
              placeholder={loadingFYs ? 'Loading…' : undefined}
            />

          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-2 px-5 py-3 bg-gray-50 dark:bg-gray-800/60 border-t border-gray-100 dark:border-gray-800">
            <button
              onClick={onClose}
              disabled={applying}
              className="px-4 py-1.5 text-[13px] font-medium text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleApply}
              disabled={applying || loadingFYs || success}
              className="flex items-center gap-1.5 px-4 py-1.5 text-[13px] font-semibold text-white rounded-lg bg-blue-600 hover:bg-blue-700 active:scale-[0.98] transition-all shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {applying ? (
                <><Loader2 size={13} className="animate-spin" /> Applying…</>
              ) : success ? (
                <><CheckCircle2 size={13} /> Done</>
              ) : (
                <>Apply</>
              )}
            </button>
          </div>

        </div>
      </div>
    </>
  );
};
